import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MdLock,
  MdQuiz,
  MdTimer,
  MdPlayArrow,
  MdArrowBack,
  MdArrowForward,
  MdCheckCircle,
  MdCancel,
  MdRemoveCircleOutline,
  MdEventBusy,
  MdEmail,
} from 'react-icons/md';
import api from '../services/api';
import toast from 'react-hot-toast';
import logo from '../assets/logo.png';
import './Assessment.css';

const PublicAssessmentLogo = () => (
  <div className="assessment-public-logo-wrap">
    <img src={logo} alt="Company logo" className="assessment-public-logo" />
  </div>
);

const PublicAssessmentFrame = ({ children, className = '' }) => (
  <div className={`assessment-public-frame${className ? ` ${className}` : ''}`}>
    <PublicAssessmentLogo />
    {children}
  </div>
);

const formatTime = (seconds) => {
  const s = Math.max(0, seconds || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const TYPE_LABELS = {
  single_select: 'Single Select',
  mcq: 'Multiple Select',
  essay: 'Essay / Writing',
  case_study: 'Case Study',
  text_input: 'Text',
};

const isAnswered = (q, ans) => {
  if (!ans) return false;
  if (q.type === 'single_select' || q.type === 'mcq') {
    return (ans.selectedOptionIds || []).length > 0;
  }
  return Boolean(String(ans.textAnswer || '').trim());
};

const formatScheduledAt = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

/** Assigned attempt that is still locked until scheduledAt */
const isWaitingForSchedule = (assessment, attempt) => {
  if (!assessment?.scheduledAt || attempt?.status !== 'assigned') return false;
  const openAt = new Date(assessment.scheduledAt);
  if (Number.isNaN(openAt.getTime())) return false;
  return Date.now() < openAt.getTime();
};

const AssessmentTake = () => {
  const { id, token } = useParams();
  const isPublic = Boolean(token);
  const apiBase = isPublic
    ? `/public/assessments/${token}`
    : `/assessments/${id}`;
  const examRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const autoSubmitted = useRef(false);
  const answersRef = useRef(answers);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  /** Block copy / cut / paste / context menu while taking the exam */
  useEffect(() => {
    if (attempt?.status !== 'in_progress') return undefined;

    const block = (e) => {
      e.preventDefault();
      return false;
    };

    const blockKeys = (e) => {
      const key = String(e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ['c', 'v', 'x', 'a', 'insert'].includes(key)) {
        e.preventDefault();
        toast.error('Copy / paste is disabled during the assessment');
        return false;
      }
      if (e.shiftKey && key === 'insert') {
        e.preventDefault();
        return false;
      }
      return undefined;
    };

    const node = examRef.current;
    const targets = [document, node].filter(Boolean);

    targets.forEach((t) => {
      t.addEventListener('copy', block, true);
      t.addEventListener('cut', block, true);
      t.addEventListener('paste', block, true);
      t.addEventListener('contextmenu', block, true);
      t.addEventListener('dragstart', block, true);
      t.addEventListener('keydown', blockKeys, true);
    });

    return () => {
      targets.forEach((t) => {
        t.removeEventListener('copy', block, true);
        t.removeEventListener('cut', block, true);
        t.removeEventListener('paste', block, true);
        t.removeEventListener('contextmenu', block, true);
        t.removeEventListener('dragstart', block, true);
        t.removeEventListener('keydown', blockKeys, true);
      });
    };
  }, [attempt?.status]);

  const hydrateAnswers = (attemptAnswers = []) => {
    const map = {};
    for (const a of attemptAnswers) {
      map[String(a.questionId)] = {
        selectedOptionIds: (a.selectedOptionIds || []).map(String),
        textAnswer: a.textAnswer || '',
      };
    }
    setAnswers(map);
  };

  const beginExamUi = (assessmentData, attemptData, keepAnswers = false) => {
    setAssessment(assessmentData);
    setAttempt({ ...attemptData });
    setRemaining(attemptData.remainingSeconds);
    autoSubmitted.current = false;
    setActiveIndex(0);
    if (!keepAnswers) {
      const map = {};
      (assessmentData.questions || []).forEach((q) => {
        map[String(q._id)] = { selectedOptionIds: [], textAnswer: '' };
      });
      setAnswers(map);
    }
    setShowStartModal(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        isPublic ? apiBase : `${apiBase}?as=assignee`,
      );
      setAssessment(data.data);
      setAttempt(data.attempt);
      if (data.attempt?.remainingSeconds != null) {
        setRemaining(data.attempt.remainingSeconds);
      }
      if (data.attempt?.answers) hydrateAnswers(data.attempt.answers);
      if (
        data.attempt?.status === 'assigned' &&
        data.attempt?.passwordVerified &&
        !isWaitingForSchedule(data.data, data.attempt)
      ) {
        setShowStartModal(true);
      } else {
        setShowStartModal(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  }, [apiBase, isPublic]);

  useEffect(() => {
    load();
  }, [load]);

  // Unlock the waiting screen when scheduledAt is reached
  useEffect(() => {
    if (!assessment?.scheduledAt || attempt?.status !== 'assigned') {
      return undefined;
    }
    const openAt = new Date(assessment.scheduledAt).getTime();
    if (Number.isNaN(openAt) || Date.now() >= openAt) return undefined;
    const delay = Math.min(openAt - Date.now() + 400, 2147483647);
    const timer = setTimeout(() => {
      load();
    }, delay);
    return () => clearTimeout(timer);
  }, [assessment?.scheduledAt, attempt?.status, load]);

  const buildPayload = () =>
    Object.entries(answersRef.current).map(([questionId, val]) => ({
      questionId,
      selectedOptionIds: val.selectedOptionIds || [],
      textAnswer: val.textAnswer || '',
    }));

  const handleSubmit = useCallback(
    async (silent = false) => {
      if (submitting || autoSubmitted.current) return;
      setSubmitting(true);
      try {
        const { data } = await api.post(`${apiBase}/submit`, {
          answers: buildPayload(),
        });
        autoSubmitted.current = true;
        setAttempt((prev) => ({ ...prev, ...data.attempt }));
        if (!silent) toast.success(data.message || 'Submitted');
        else toast('Time is up — assessment submitted automatically');
        await load();
      } catch (err) {
        if (!silent) {
          toast.error(err.response?.data?.message || 'Submit failed');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [apiBase, load, submitting],
  );

  useEffect(() => {
    if (attempt?.status !== 'in_progress' || remaining == null) return undefined;
    const tick = setInterval(() => {
      setRemaining((prev) => {
        if (prev == null) return prev;
        if (prev <= 1) {
          clearInterval(tick);
          if (!autoSubmitted.current) handleSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [attempt?.status, remaining, handleSubmit]);

  useEffect(() => {
    if (attempt?.status !== 'in_progress') return undefined;
    const save = setInterval(() => {
      api
        .put(`${apiBase}/answers`, { answers: buildPayload() })
        .then(({ data }) => {
          if (data.remainingSeconds != null) setRemaining(data.remainingSeconds);
        })
        .catch(() => {});
    }, 20000);
    return () => clearInterval(save);
  }, [attempt?.status, apiBase]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const { data } = await api.post(`${apiBase}/verify-password`, {
        password,
      });
      if (data.alreadyStarted && data.assessment) {
        beginExamUi(data.assessment, data.attempt, true);
        if (data.attempt?.answers) hydrateAnswers(data.attempt.answers);
        toast.success('Continuing your assessment');
        return;
      }
      setAttempt((prev) => ({
        ...prev,
        ...data.attempt,
        passwordVerified: true,
      }));
      setShowStartModal(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Incorrect password');
    } finally {
      setVerifying(false);
    }
  };

  const handleStartAssessment = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`${apiBase}/start`);
      beginExamUi(data.assessment, data.attempt, false);
      toast.success('Timer started — good luck!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start assessment');
    } finally {
      setStarting(false);
    }
  };

  const setObjective = (questionId, optionId, multi) => {
    setAnswers((prev) => {
      const cur = prev[questionId] || { selectedOptionIds: [], textAnswer: '' };
      let selected = [...(cur.selectedOptionIds || [])];
      if (multi) {
        selected = selected.includes(optionId)
          ? selected.filter((x) => x !== optionId)
          : [...selected, optionId];
      } else {
        selected = [optionId];
      }
      return { ...prev, [questionId]: { ...cur, selectedOptionIds: selected } };
    });
  };

  const startModal = showStartModal && (
    <div className="assessment-modal-overlay" role="dialog" aria-modal="true">
      <div className="assessment-modal">
        <div className="assessment-modal-icon">
          <MdTimer size={36} />
        </div>
        <h2>Ready to begin</h2>
        <p>
          Password verified for <strong>{assessment?.title}</strong>.
        </p>
        <p>
          When you click <strong>Start assessment</strong>, the timer will start
          ({assessment?.durationMinutes} minutes) and you can answer the questions.
        </p>
        <button
          type="button"
          className="assessment-btn"
          disabled={starting}
          onClick={handleStartAssessment}
        >
          <MdPlayArrow size={20} />
          {starting ? 'Starting…' : 'Start assessment'}
        </button>
      </div>
    </div>
  );

  const publicShellClass = isPublic ? ' assessment-take-public' : '';

  const centeredContent = (content) =>
    isPublic ? (
      <PublicAssessmentFrame>{content}</PublicAssessmentFrame>
    ) : (
      content
    );

  if (loading) {
    return (
      <div
        className={`assessment-take-shell assessment-take-centered${publicShellClass}`}
      >
        {centeredContent(
          <div className="assessment-loading">
            <span className="spinner" /> Loading…
          </div>,
        )}
      </div>
    );
  }

  if (!assessment || !attempt) {
    return (
      <div
        className={`assessment-take-shell assessment-take-centered${publicShellClass}`}
      >
        {centeredContent(
          <div className="assessment-empty">
            Assessment not found or link is invalid.
            {!isPublic && (
              <>
                {' '}
                <Link to="/assessments">Back</Link>
              </>
            )}
          </div>,
        )}
      </div>
    );
  }

  if (attempt.status === 'assigned') {
    if (isWaitingForSchedule(assessment, attempt)) {
      return (
        <div
          className={`assessment-take-shell assessment-take-centered${publicShellClass}`}
        >
          {centeredContent(
            <div className="assessment-password-card assessment-password-card-centered">
              <h1>
                <MdEventBusy /> {assessment.title}
              </h1>
              <p className="assessment-subtitle">
                This assessment is not available yet
              </p>
              <p className="assessment-password-hint">
                You can open it on{' '}
                <strong>{formatScheduledAt(assessment.scheduledAt)}</strong>.
                Please come back at the scheduled date and time.
              </p>
              <div className="assessment-actions" style={{ marginTop: 20 }}>
                {isPublic ? (
                  <p className="assessment-password-hint" style={{ margin: 0 }}>
                    Taking as <strong>{attempt.attendeeEmail}</strong>
                  </p>
                ) : (
                  <Link to="/assessments" className="assessment-btn secondary">
                    <MdArrowBack size={18} />
                    Back to assigned
                  </Link>
                )}
              </div>
            </div>,
          )}
        </div>
      );
    }

    return (
      <div
        className={`assessment-take-shell assessment-take-centered${publicShellClass}`}
      >
        {centeredContent(
          <div className="assessment-password-card assessment-password-card-centered">
            <h1>
              <MdLock /> {assessment.title}
            </h1>
            <p className="assessment-subtitle">
              {assessment.scheduledAt
                ? `Scheduled: ${formatScheduledAt(assessment.scheduledAt)} · `
                : ''}
              Duration: {assessment.durationMinutes} minutes · Total marks:{' '}
              {assessment.totalMarks}
            </p>
            <p className="assessment-password-hint">
              {isPublic && attempt.attendeeEmail
                ? `Signed in as ${attempt.attendeeEmail}. `
                : ''}
              Enter the password provided by the creator to unlock this assessment.
            </p>
            <form onSubmit={handleVerify} className="assessment-password-form">
              <div className="assessment-field">
                <label>Assessment password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  disabled={Boolean(attempt.passwordVerified) || showStartModal}
                />
              </div>
              <button
                type="submit"
                className="assessment-btn"
                disabled={
                  verifying || Boolean(attempt.passwordVerified) || showStartModal
                }
              >
                {verifying ? 'Verifying…' : 'Unlock assessment'}
              </button>
            </form>
          </div>,
        )}
        {startModal}
      </div>
    );
  }

  if (['submitted', 'evaluated'].includes(attempt.status)) {
    const questions = assessment.questions || [];
    const answerByQ = Object.fromEntries(
      (attempt.answers || []).map((a) => [String(a.questionId), a]),
    );
    const pct = assessment.totalMarks
      ? Math.round((Number(attempt.totalScore || 0) / assessment.totalMarks) * 100)
      : 0;
    const correctCount = questions.filter((q) => {
      if (q.type !== 'single_select' && q.type !== 'mcq') return false;
      const ans = answerByQ[String(q._id)] || {};
      const selected = (ans.selectedOptionIds || []).map(String);
      const correctIds = (q.options || [])
        .filter((o) => o.isCorrect)
        .map((o) => String(o._id));
      return (
        correctIds.length > 0 &&
        correctIds.length === selected.length &&
        correctIds.every((id) => selected.includes(id))
      );
    }).length;
    const objectiveCount = questions.filter(
      (q) => q.type === 'single_select' || q.type === 'mcq',
    ).length;
    const isEvaluated = attempt.status === 'evaluated';

    return (
      <div
        className={`assessment-result-page${
          isPublic ? ' assessment-result-page--public' : ''
        }`}
      >
        <div className={isPublic ? 'assessment-result-public-wrap' : undefined}>
          {isPublic ? (
            <header className="assessment-result-public-top">
              <PublicAssessmentLogo />
            </header>
          ) : null}
          <div className="assessment-result-layout">
          <aside className="assessment-result-sidebar">
            <header className="assessment-result-hero">
              <div className="assessment-result-hero-top">
                <div className="assessment-result-hero-copy">
                  <div className="assessment-result-status-row">
                    <span className="assessment-result-kicker">
                      Assessment result
                    </span>
                    <span
                      className={`assessment-result-status-chip ${
                        isEvaluated ? 'done' : 'pending'
                      }`}
                    >
                      {isEvaluated ? 'Fully evaluated' : 'Under review'}
                    </span>
                    {isPublic && attempt?.attendeeEmail ? (
                      <span
                        className="assessment-result-attendee-chip"
                        title="Submitted as"
                      >
                        <MdEmail size={14} />
                        {attempt.attendeeEmail}
                      </span>
                    ) : null}
                  </div>
                  <h1>
                    <span className="assessment-result-title-icon">
                      <MdQuiz size={22} />
                    </span>
                    {assessment.title}
                  </h1>
                  <p className="assessment-result-meta">
                    {isEvaluated
                      ? 'All answers have been scored.'
                      : 'Descriptive answers may still be under review.'}
                    {attempt.submittedAt
                      ? ` Submitted ${new Date(attempt.submittedAt).toLocaleString()}.`
                      : ''}
                  </p>
                </div>

                <div
                  className="assessment-result-score-circle"
                  style={{ '--pct': `${Math.min(100, Math.max(0, pct))}` }}
                  aria-label={`Score ${attempt.totalScore ?? 0} of ${assessment.totalMarks}, ${pct} percent`}
                >
                  <div className="assessment-result-score-circle-inner">
                    <strong>{attempt.totalScore ?? 0}</strong>
                    <span>of {assessment.totalMarks}</span>
                    <em>{pct}%</em>
                  </div>
                </div>
              </div>

              <div className="assessment-result-stats">
                <div>
                  <span>Auto score</span>
                  <strong>{attempt.autoScore ?? 0}</strong>
                </div>
                <div>
                  <span>Manual score</span>
                  <strong>{attempt.manualScore ?? 0}</strong>
                </div>
                <div>
                  <span>Correct</span>
                  <strong>
                    {correctCount}
                    <small>/{objectiveCount || questions.length}</small>
                  </strong>
                </div>
                <div>
                  <span>Duration</span>
                  <strong>{assessment.durationMinutes} min</strong>
                </div>
              </div>
            </header>

            {!isPublic ? (
              <div className="assessment-result-actions assessment-result-actions--sidebar">
                <Link to="/assessments" className="assessment-btn secondary">
                  <MdArrowBack size={18} />
                  Back to assigned
                </Link>
              </div>
            ) : null}
          </aside>

          <main className="assessment-result-main">
            <section className="assessment-result-list">
              <div className="assessment-result-list-head">
                <h2>Question review</h2>
                <span>{questions.length} questions</span>
              </div>

              {questions.map((q, idx) => {
                const ans = answerByQ[String(q._id)] || {};
                const selected = (ans.selectedOptionIds || []).map(String);
                const isObjective =
                  q.type === 'single_select' || q.type === 'mcq';
                const correctIds = (q.options || [])
                  .filter((o) => o.isCorrect)
                  .map((o) => String(o._id));
                const isCorrect =
                  isObjective &&
                  correctIds.length > 0 &&
                  correctIds.length === selected.length &&
                  correctIds.every((cid) => selected.includes(cid));
                const marksGot = isObjective
                  ? Number(ans.autoMarks || 0)
                  : ans.isManuallyGraded
                    ? Number(ans.manualMarks || 0)
                    : null;

                return (
                  <article
                    key={q._id}
                    className={`assessment-result-q ${
                      isObjective
                        ? isCorrect
                          ? 'correct'
                          : 'wrong'
                        : 'manual'
                    }`}
                    style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
                  >
                    <div className="assessment-result-q-index">{idx + 1}</div>
                    <div className="assessment-result-q-body">
                      <div className="assessment-result-q-head">
                        <div>
                          <div className="assessment-result-q-badges">
                            <span className="assessment-exam-pill">
                              {TYPE_LABELS[q.type] || q.type}
                            </span>
                            <span className="assessment-exam-pill marks">
                              {marksGot == null
                                ? `Pending / ${q.marks}`
                                : `${marksGot} / ${q.marks}`}
                            </span>
                            {isObjective && (
                              <span
                                className={`assessment-result-verdict ${
                                  isCorrect ? 'ok' : 'bad'
                                }`}
                              >
                                {isCorrect ? (
                                  <>
                                    <MdCheckCircle size={15} /> Correct
                                  </>
                                ) : (
                                  <>
                                    <MdCancel size={15} /> Incorrect
                                  </>
                                )}
                              </span>
                            )}
                            {!isObjective && !ans.isManuallyGraded && (
                              <span className="assessment-result-verdict pending">
                                <MdRemoveCircleOutline size={15} /> Awaiting
                                review
                              </span>
                            )}
                          </div>
                          <h3>{q.text}</h3>
                        </div>
                      </div>

                      {isObjective ? (
                        <div className="assessment-result-options">
                          {(q.options || []).map((o, oi) => {
                            const oid = String(o._id);
                            const letter = String.fromCharCode(65 + oi);
                            const userPicked = selected.includes(oid);
                            const isRight = Boolean(o.isCorrect);
                            return (
                              <div
                                key={oid}
                                className={[
                                  'assessment-result-option',
                                  isRight ? 'is-correct' : '',
                                  userPicked && !isRight
                                    ? 'is-wrong-pick'
                                    : '',
                                  userPicked && isRight
                                    ? 'is-right-pick'
                                    : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                <span className="assessment-exam-letter">
                                  {letter}
                                </span>
                                <span className="assessment-result-option-text">
                                  {o.text}
                                </span>
                                <span className="assessment-result-option-tags">
                                  {userPicked && <em>Your answer</em>}
                                  {isRight && (
                                    <em className="right">Correct</em>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="assessment-result-essay">
                          <p className="assessment-result-essay-label">
                            Your answer
                          </p>
                          <div className="assessment-result-essay-body">
                            {ans.textAnswer?.trim()
                              ? ans.textAnswer
                              : '(No answer submitted)'}
                          </div>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </section>
          </main>
        </div>
        </div>
      </div>
    );
  }

  const questions = assessment.questions || [];
  const current = questions[activeIndex];
  const qid = current ? String(current._id) : '';
  const ans = answers[qid] || { selectedOptionIds: [], textAnswer: '' };
  const answeredCount = questions.filter((q) =>
    isAnswered(q, answers[String(q._id)]),
  ).length;
  const progressPct = questions.length
    ? Math.round((answeredCount / questions.length) * 100)
    : 0;

  return (
    <div
      className={`assessment-exam assessment-no-copy${
        isPublic ? ' assessment-exam--public' : ''
      }`}
      ref={examRef}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {isPublic ? (
        <div className="assessment-exam-public-brand">
          <PublicAssessmentLogo />
        </div>
      ) : null}
      <header
        className={`assessment-exam-topbar ${
          remaining != null && remaining <= 60 ? 'warn' : ''
        }`}
      >
        <div className="assessment-exam-topbar-inner">
          <div className="assessment-exam-brand">
            {!isPublic ? (
              <span className="assessment-exam-brand-icon">
                <MdQuiz size={22} />
              </span>
            ) : null}
            <div className="assessment-exam-brand-copy">
              <h1>{assessment.title}</h1>
              <p>
                {answeredCount}/{questions.length} answered · {assessment.totalMarks}{' '}
                total marks
              </p>
            </div>
          </div>

          <div className="assessment-exam-stats">
            <div className="assessment-exam-stat">
              <span className="assessment-exam-stat-label">Question</span>
              <span className="assessment-exam-stat-value">
                {activeIndex + 1}
                <small>/{questions.length}</small>
              </span>
            </div>
            <div
              className={`assessment-exam-timer ${
                remaining != null && remaining <= 60 ? 'urgent' : ''
              }`}
            >
              <MdTimer size={20} />
              <div>
                <span className="assessment-exam-stat-label">Time left</span>
                <strong>{formatTime(remaining)}</strong>
              </div>
            </div>
          </div>
        </div>
        <div className="assessment-exam-progress">
          <div
            className="assessment-exam-progress-bar"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </header>

      <div className="assessment-exam-layout">
        <main className="assessment-exam-main">
          {current && (
            <div className="assessment-exam-card">
              <div className="assessment-exam-card-head">
                <span className="assessment-exam-qnum">
                  Question {activeIndex + 1}
                </span>
                <div className="assessment-exam-card-meta">
                  <span className="assessment-exam-pill">
                    {TYPE_LABELS[current.type] || current.type}
                  </span>
                  <span className="assessment-exam-pill marks">
                    {current.marks} marks
                  </span>
                </div>
              </div>

              <h2 className="assessment-exam-question-text">{current.text}</h2>

              {(current.type === 'single_select' || current.type === 'mcq') && (
                <div className="assessment-exam-options">
                  {(current.options || []).map((o, oi) => {
                    const oid = String(o._id);
                    const checked = (ans.selectedOptionIds || []).includes(oid);
                    const letter = String.fromCharCode(65 + oi);
                    return (
                      <label
                        key={oid}
                        className={`assessment-exam-option ${
                          checked ? 'selected' : ''
                        }`}
                      >
                        <input
                          type={current.type === 'mcq' ? 'checkbox' : 'radio'}
                          name={`q-${qid}`}
                          checked={checked}
                          onChange={() =>
                            setObjective(qid, oid, current.type === 'mcq')
                          }
                        />
                        <span className="assessment-exam-letter">{letter}</span>
                        <span className="assessment-exam-option-text">
                          {o.text}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {(current.type === 'essay' ||
                current.type === 'case_study' ||
                current.type === 'text_input') && (
                <div className="assessment-exam-write">
                  <p className="assessment-exam-write-hint">
                    Type your answer below. Copy &amp; paste is disabled.
                  </p>
                  <textarea
                    rows={12}
                    value={ans.textAnswer}
                    onChange={(e) =>
                      setAnswers((prev) => ({
                        ...prev,
                        [qid]: {
                          ...(prev[qid] || {}),
                          textAnswer: e.target.value,
                          selectedOptionIds: [],
                        },
                      }))
                    }
                    onPaste={(e) => {
                      e.preventDefault();
                      toast.error('Paste is disabled during the assessment');
                    }}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    placeholder="Start typing…"
                    className="assessment-answer-textarea assessment-exam-textarea"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
              )}
            </div>
          )}

          <div className="assessment-exam-footer">
            <button
              type="button"
              className="assessment-btn secondary"
              disabled={activeIndex === 0}
              onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
            >
              <MdArrowBack size={18} />
              Previous
            </button>

            <div className="assessment-exam-footer-mid">
              {isAnswered(current, ans) ? (
                <span className="assessment-exam-answered-tag">
                  <MdCheckCircle size={16} /> Answered
                </span>
              ) : (
                <span className="assessment-exam-unanswered-tag">
                  Not answered yet
                </span>
              )}
            </div>

            {activeIndex < questions.length - 1 ? (
              <button
                type="button"
                className="assessment-btn"
                onClick={() =>
                  setActiveIndex((i) => Math.min(questions.length - 1, i + 1))
                }
              >
                Next question
                <MdArrowForward size={18} />
              </button>
            ) : (
              <button
                type="button"
                className="assessment-btn"
                disabled={submitting}
                onClick={() => {
                  if (
                    window.confirm(
                      'Submit assessment? You cannot change answers after this.',
                    )
                  ) {
                    handleSubmit(false);
                  }
                }}
              >
                {submitting ? 'Submitting…' : 'Submit assessment'}
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AssessmentTake;
